/*
<summary>
   This functional component is all about rendering the Defective Items CSV
   import popup and submitting the selected file to the upload API.
   Developer: Aashish Singh, Created Date: 05-Oct-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IFormModalProps } from '../../../models/forms/IForm';
import { IImportDefectiveItem } from '../../../models/forms/IImportDefectiveItem';
import DefaultModal from '../../../shared-components/modal/default-modal';
import { formatMessage } from '../../../translations/format-message';
import ImportDefectiveItemForm from '../forms/import-defective-item-form';

/**
* This functional component renders the CSV import popup for Defective Items and
* submits the selected file to the upload service.
* @param props: Modal identifier and close handler
*/
const ImportDefectiveItem = (props: IFormModalProps) => {
  const initialValues: IImportDefectiveItem = { csvFile: null };
  const { defectiveItemsStore } = useStore();
  const { DefectiveItemFileUploadService, fileUploadState, resetFileUploadState } =
    defectiveItemsStore;

  useEffect(() => {
    if (fileUploadState.success) {
      onModalClosed();
    }
  }, [fileUploadState.success]);

  useEffect(() => {
    if (fileUploadState.error) {
      if (fileUploadState.error === 'PartialSuccess') {
        toast.success(formatMessage('PartialSuccess'));
        onModalClosed();
      } else {
        toast.error(formatMessage(fileUploadState.error));
        resetFileUploadState();
      }
    }
  }, [fileUploadState.error]);

  const onSubmitForm = (values: IImportDefectiveItem) => {
    if (values.csvFile) {
      DefectiveItemFileUploadService(
        Number(getTenantIdFromLocalStorage()),
        values.csvFile
      );
    }
  };

  const onModalClosed = () => {
    resetFileUploadState();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <DefaultModal
        id={props.id}
        size="sm"
        className="modal-sm fancy-modal"
        addHeader="import_defective_items"
        updateHeader="import_defective_items"
        onModalClosed={onModalClosed}
        isLoading={fileUploadState.inProgress}
      >
        <ImportDefectiveItemForm
          initialValues={initialValues}
          id={props.id}
          submitHandler={onSubmitForm}
          onModalClosed={onModalClosed}
          isLoading={fileUploadState.inProgress}
        />
      </DefaultModal>
    </React.Fragment>
  );
};

export default observer(ImportDefectiveItem);
